import { Period } from '@spike/model';
import { useTimeZone } from 'hooks';
import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useState } from 'react';
import Header from '../../components/Header';
import LocationFilter from '../components/LocationFilter';
import MetricDelta from '../components/MetricDelta';
import { formatCurrency, formatPercentage, getOverviewData } from '../data/mockData';
import { ClientsBySourceBarChart } from './charts/ClientsBySourceBarChart';
import LocationComparisonTable from './LocationComparisonTable';
import styles from './MarketingOverview.module.scss';
import SourcePerformanceTable from './SourcePerformanceTable';

interface MarketingOverviewProps {
    description?: string;
}

export const MarketingOverview: FunctionComponent<MarketingOverviewProps> = props => {
    const timeZone = useTimeZone();

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).subtract(29, 'days').startOf('day'),
        to: moment().tz(timeZone).endOf('day')
    });
    const [selectedLocationIds, setSelectedLocationIds] = useState<Array<number>>([]);
    const [loading, setLoading] = useState(true);

    const data = getOverviewData(selectedLocationIds);

    useEffect(() => {
        setLoading(true);
        const timeout = setTimeout(() => setLoading(false), 600);
        return () => clearTimeout(timeout);
    }, [period, selectedLocationIds]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        setPeriod(
            allTime
                ? null
                : {
                      from: dateFilter?.from || moment(),
                      to: dateFilter?.to || moment()
                  }
        );
    };

    const csvHeaders = [
        'Source',
        'Leads',
        'New Clients',
        'Conv. Rate',
        'Days to Book',
        'First Visit Revenue',
        '90-Day Revenue',
        'Avg. First Ticket',
        'Rebook Rate'
    ];

    const exportFile = {
        name: 'Marketing Overview - Cuddles.csv',
        dataInMemory: true,
        headers: csvHeaders,
        dataToExport: data.rows.map(row => [
            row.source.name,
            row.leads.toString(),
            row.newClients.toString(),
            formatPercentage(row.convRate),
            row.medianDaysToBook.toString(),
            formatCurrency(row.revenueFirst),
            formatCurrency(row.revenue90d),
            formatCurrency(row.aov),
            formatPercentage(row.rebookRate)
        ])
    };

    return (
        <div className={styles.container}>
            <span className={styles.description}>{props.description}</span>

            <div className={styles.toolbar}>
                <LocationFilter
                    selected={selectedLocationIds}
                    onChangeSelection={setSelectedLocationIds}
                />
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={{ optionSelected: 'Last 30 days' }}
                />
            </div>

            <div className={styles.metrics}>
                <MetricDelta
                    title="New Clients"
                    value={data.totalNewClients.toString()}
                    delta={data.totalNewClientsDelta}
                    isLoading={loading}
                />
                <MetricDelta
                    title="90-Day Revenue"
                    value={formatCurrency(data.totalRevenue90d)}
                    delta={data.totalRevenue90dDelta}
                    isLoading={loading}
                />
                <MetricDelta
                    title="Avg. Client Acquisition Cost"
                    value={formatCurrency(data.avgCac)}
                    delta={data.avgCacDelta}
                    invertColors
                    isLoading={loading}
                />
                <MetricDelta
                    title="Unattributed"
                    value={formatPercentage(data.unattributedRate)}
                    isLoading={loading}
                />
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>New Clients by Source</h3>
                <div className={styles.chartCard}>
                    <ClientsBySourceBarChart
                        rows={data.rows}
                        loading={loading}
                    />
                </div>
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Source Performance</h3>
                <SourcePerformanceTable
                    rows={data.rows}
                    loading={loading}
                />
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Acquisition Mix by Location</h3>
                <p className={styles.sectionHint}>Share of attributed new clients per source, at each location.</p>
                <LocationComparisonTable
                    locationIds={selectedLocationIds}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default MarketingOverview;
